import { useOptionalPrivanaI18n as usePortalPresentationI18n } from '@/i18n/PrivanaI18nProvider.jsx'
import LetsPlayRacquetSetup from '../components/LetsPlayRacquetSetup'
import React, { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ChevronRight, Clock3, Flag } from 'lucide-react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { normalizeMemberFacilities } from '../lib/normalizeMemberFacilities'
import { readMemberResource, writeMemberResource } from '../lib/resourceCache'
import { resolveEligibleLetsPlaySport, resolveLetsPlaySports } from '../lib/letsPlaySports'
import LetsPlayGolfSetup from '../components/LetsPlayGolfSetup'
import { membersNavigationState, useLetsPlayBack } from '../lib/letsPlayNavigation'
import LetsPlayLoading from '../components/LetsPlayLoading'

export default function MemberLetsPlayPage({ clubCode }) {
  const { t: portalText } = usePortalPresentationI18n()

  const { sportSlug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth0()
  const { memberPortalApiGet } = useMemberPortalApi()
  const cacheScope = useMemo(() => ({ clubCode, memberIdentity: user?.sub }), [clubCode, user?.sub])
  const cached = useMemo(() => readMemberResource(cacheScope, 'facilities', { ttlMs: 30 * 60 * 1000 }), [cacheScope])
  const [facilities, setFacilities] = useState(() => normalizeMemberFacilities(cached?.data))
  const [loading, setLoading] = useState(!cached?.data)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  const [configurationResolved, setConfigurationResolved] = useState(false)
  const sports = useMemo(() => resolveLetsPlaySports(facilities), [facilities])
  const selectedSport = resolveEligibleLetsPlaySport(sports, sportSlug)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setConfigurationResolved(false)
    setError('')
    memberPortalApiGet(`/api/members-portal/${encodeURIComponent(clubCode)}/facilities`)
      .then(async (response) => {
        const payload = await response.json().catch(() => null)
        if (!response.ok) throw new Error(payload?.error || 'facilities_unavailable')
        if (!cancelled) {
          const next = normalizeMemberFacilities(payload?.data)
          setFacilities(next)
          writeMemberResource(cacheScope, 'facilities', next)
        }
      })
      .catch(() => { if (!cancelled) setError('facilities_unavailable') })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
          setConfigurationResolved(true)
        }
      })
    return () => { cancelled = true }
  }, [cacheScope, clubCode, memberPortalApiGet, retry])

  useEffect(() => {
    if (!configurationResolved || !sportSlug || selectedSport) return
    navigate(`/${clubCode}/lets-play`, { replace: true })
  }, [clubCode, configurationResolved, navigate, selectedSport, sportSlug])

  const back = useLetsPlayBack(clubCode, sportSlug ? `/${clubCode}/lets-play` : `/${clubCode}/dashboard`)
  const navigateInFlow = (to) => navigate(to, { state: membersNavigationState(location) })

  return (
    <section className="mp-lets-play" aria-labelledby="lets-play-title">
      {!sportSlug && <header className="mp-lets-play__header">
        <button className="mp-lets-play__return" type="button" onClick={back}>
          <ArrowLeft size={18} aria-hidden="true" /> {portalText("roomScreen.back")}
        </button>
        <span className="mp-lets-play__brand">{portalText("memberRemaining.m54")}</span>
      </header>}

      {sportSlug && selectedSport ? (
        selectedSport.slug === 'golf' ? <LetsPlayGolfSetup clubCode={clubCode} onBackToSports={back} /> : <LetsPlayRacquetSetup key={selectedSport.slug} clubCode={clubCode} sport={selectedSport.slug} onBackToSports={back} />
      ) : sportSlug ? (
        <LetsPlayLoading label={portalText("memberWorkflows.golf.checkingSportAvailability")} />
      ) : (
        <>
          <div className="mp-lets-play__intro">
            <h1 id="lets-play-title">{portalText("memberRemaining.m55")}</h1>
          </div>
          {loading && sports.length === 0 ? (
            <LetsPlayLoading label={portalText("memberWorkflows.golf.loadingClubSports")} detail={portalText("memberWorkflows.golf.findingWhatSAvailableToday")} />
          ) : error && sports.length === 0 ? (
            <div className="mp-lets-play__state" role="alert"><h2>{portalText("memberRemaining.m56")}</h2><button type="button" onClick={() => setRetry((value) => value + 1)}>{portalText("achievements.retry")}</button></div>
          ) : sports.length === 0 ? (
            <div className="mp-lets-play__state"><h2>{portalText("membersHome.play")}</h2><p>{portalText("memberRemaining.m57")}</p></div>
          ) : (
            <>{error ? <div className="mp-lets-play__stale" role="status">{portalText("memberRemaining.m58")} <button type="button" onClick={() => setRetry((value) => value + 1)}>{portalText("achievements.retry")}</button></div> : null}<div className="mp-lets-play__tiles">{sports.map((sport, index) => <LetsPlaySportTile key={sport.id} sport={sport} index={index} onSelect={() => navigateInFlow(`/${clubCode}/lets-play/${sport.slug}`)} />)}</div><button className="mp-lets-play__history" onClick={()=>navigateInFlow(`/${clubCode}/lets-play/history`)}><Clock3 aria-hidden="true"/> {portalText("memberRemaining.m59")} <ChevronRight aria-hidden="true"/></button></>
          )}
        </>
      )}
    </section>
  )
}

export function LetsPlaySportTile({ sport, index = 0, onSelect }) {
  const { t: portalText } = usePortalPresentationI18n()

  const [imageFailed, setImageFailed] = useState(false)
  useEffect(() => setImageFailed(false), [sport.imageUrl])
  const showImage = Boolean(sport.imageUrl) && !imageFailed
  return (
    <button type="button" className={`mp-lets-play__tile${showImage ? ' has-image' : ' is-fallback'}`} style={{ '--mp-play-tile-index': index }} onClick={onSelect}>
      {showImage ? <img src={sport.imageUrl} alt="" onError={() => setImageFailed(true)} style={{ objectPosition: `${sport.imageFocus.x}% ${sport.imageFocus.y}%` }} /> : <span className="mp-lets-play__fallback"><Flag size={40} aria-hidden="true" /><span>{portalText("memberRemaining.m60")}</span></span>}
      <span className="mp-lets-play__tile-gradient" aria-hidden="true" />
      <span className="mp-lets-play__tile-copy"><strong>{sport.titleKey ? portalText(sport.titleKey) : sport.title}</strong><span>{sport.key === 'golf' ? portalText("memberWorkflows.golf.playARound") : portalText("memberWorkflows.golf.takeToTheCourt")}</span></span>
      <ChevronRight className="mp-lets-play__chevron" aria-hidden="true" />
    </button>
  )
}
